// PaymentHandler.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaPhone, FaGoogle, FaPaypal } from 'react-icons/fa';
import { SiPhonepe, SiGooglepay, SiPaytm } from 'react-icons/si';
import { IoClose } from 'react-icons/io5';
import { AiOutlineLoading } from 'react-icons/ai';
import { apiCall } from '../utils/api';
import { APP_CONFIG } from '../app-config';
import toast, { Toaster } from 'react-hot-toast';

// Razorpay Web SDK
const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const COLORS = APP_CONFIG.colors;

export const PaymentHandler = ({
    result,
    onPaymentComplete,
    onPaymentError
}) => {
    const navigate = useNavigate();
    const [paymentOrderID, setPaymentOrderID] = useState(result?.order_info?.order_id);
    const [intentParams, setIntentParams] = useState(null);
    const [showIntentSheet, setShowIntentSheet] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isStatusCheckActive, setIsStatusCheckActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusCheckCount, setStatusCheckCount] = useState(0);
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
    const [razorpayComplete, setRazorpayComplete] = useState(false);
    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

    const statusCheckIntervalRef = useRef(null);
    const MAX_STATUS_CHECKS = 24;
    const CHECK_INTERVAL = 5000;
    const MAX_WAIT_TIME = MAX_STATUS_CHECKS * CHECK_INTERVAL;

    const apps = [
        {
            id: 'phonepe',
            name: 'PhonePe',
            intentUrl: `phonepe://pay?${intentParams || ''}`,
            icon: <SiPhonepe size={40} color="#673ab7" />,
            color: '#673ab7'
        },
        {
            id: 'google_pay',
            name: 'Google Pay',
            intentUrl: `tez://upi/pay?${intentParams || ''}`,
            icon: <SiGooglepay size={40} color="#4285F4" />,
            color: '#4285F4'
        },
        {
            id: 'paytm',
            name: 'Paytm',
            intentUrl: `paytmmp://pay?${intentParams || ''}`,
            icon: <SiPaytm size={40} color="#02b9ef" />,
            color: '#02b9ef'
        },
        {
            id: 'bhim',
            name: 'UPI (BHIM)',
            intentUrl: `upi://pay?${intentParams || ''}`,
            icon: <FaPaypal size={40} color="#098041" />,
            color: '#098041'
        }
    ];

    // Load Razorpay script on mount
    useEffect(() => {
        const loadScript = async () => {
            const loaded = await loadRazorpayScript();
            setIsRazorpayLoaded(loaded);
            if (!loaded) {
                toast.error('Failed to load payment gateway');
            }
        };
        loadScript();
    }, []);

    // Process payment when component mounts or result changes
    useEffect(() => {
        if (result && !isProcessing) {
            handlePayment();
        }
    }, [result]);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            stopStatusCheck();
        };
    }, []);

    const handlePayment = () => {
        setIsProcessing(true);

        try {
            if (result?.order_info?.payment_method === "intent") {
                handleIntentPayment();
            } else if (result?.order_info?.payment_method === "razorpay-sdk") {
                handleRazorpayPayment();
            } else if (result?.order_info?.payment_method === "web") {
                handleWebPayment();
            } else {
                if (onPaymentError) onPaymentError('Unsupported payment method');
            }
        } catch (error) {
            if (onPaymentError) onPaymentError(error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleWebPayment = async () => {
        const payment_url = result?.order_info?.payment_url;

        if (!payment_url) {
            console.error("Payment URL not found");
            return;
        }

        window.open(payment_url, "_blank", "noopener,noreferrer");
    };

    const handleIntentPayment = async () => {
        const payment_url = result?.order_info?.payment_url;
        if (!payment_url) {
            toast.error('Payment URL not found');
            return;
        }

        const params = payment_url.replace('upi://pay', '') || '';
        setIntentParams(params);
        setShowIntentSheet(true);
    };

    const handleRazorpayPayment = () => {
        if (!isRazorpayLoaded) {
            toast.error('Payment gateway is loading. Please try again.');
            return;
        }

        const options = {
            description: 'Payment to Paywon',
            image: 'https://paywon.in/assets/logo.png',
            currency: 'INR',
            key: 'rzp_live_TTh5SSt3pUitcG',
            amount: result?.order_info?.amount * 100,
            name: 'Paywon',
            order_id: result?.order_info?.payment_id,
            prefill: {
                email: result?.user_info?.email,
                contact: result?.user_info?.contact,
                name: result?.user_info?.name,
            },
            theme: { color: COLORS.primaryColor },
            modal: {
                ondismiss: function () {
                    // Handle modal dismiss
                    if (onPaymentError) onPaymentError('Payment cancelled');
                }
            },
            config: {
                display: {
                    blocks: {
                        upi: {
                            name: 'Pay using UPI',
                            instruments: [
                                {
                                    method: 'upi',
                                },
                            ],
                        },
                    },
                    sequence: ['block.upi'],
                    preferences: {
                        show_default_blocks: false,
                    },
                },
            },
        };

        const razorpay = new window.Razorpay(options);

        razorpay.on('payment.success', function (response) {
            setRazorpayComplete(true);
            startStatusCheck();
        });

        razorpay.on('payment.error', function (response) {
            if (onPaymentError) onPaymentError(response.error.description);
        });

        razorpay.open();
    };

    const handleAppSelect = async (app) => {
        try {
            setLoading(true);

            // For web, we'll use window.location or window.open for UPI links
            // Note: UPI deep linking may not work on all browsers
            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

            if (!isMobile) {
                setLoading(false);
                toast.error('UPI payments are only supported on mobile devices');
                return;
            }

            // Try to open the UPI app
            const url = app.intentUrl;

            // For web, we use window.location to trigger the deep link
            window.location.href = url;

            setLoading(false);

            // Start status check after app is opened
            setTimeout(() => {
                startStatusCheck();
            }, 2000);

        } catch (error) {
            toast.error('Failed to open payment app. Please try again');
            setLoading(false);
        }
    };

    const checkStatus = async () => {
        try {
            const result = await apiCall(
                "payment-order-id-status",
                {
                    payment_order_id: paymentOrderID
                },
                true
            );

            if (result.status && result.order_details?.status === "Success") {
                closeIntentSheet();
                stopStatusCheck();
                setRazorpayComplete(false);
                setIsPaymentCompleted(true);
                onPaymentComplete(result);
                return true;
            }

            if (result.order_details?.status === "Failed") {
                closeIntentSheet();
                stopStatusCheck();
                setRazorpayComplete(false);
                setIsPaymentCompleted(true);
                onPaymentComplete(result);
                return true;
            }

            return null;
        } catch (error) {
            closeIntentSheet();
            stopStatusCheck();
            setRazorpayComplete(false);
            setIsPaymentCompleted(true);
            onPaymentError(error.message);
            return false;
        }
    };

    const startStatusCheck = () => {
        if (statusCheckIntervalRef.current) {
            return;
        }

        console.log('Starting status check with interval:', CHECK_INTERVAL, 'ms');

        setStatusCheckCount(0);
        setIsStatusCheckActive(true);
        setLoading(true);

        statusCheckIntervalRef.current = setInterval(async () => {
            setStatusCheckCount(prevCount => prevCount + 1);

            console.log('Status check attempt:', statusCheckCount + 1);

            if (statusCheckCount >= MAX_STATUS_CHECKS) {
                console.log('Max attempts reached, stopping...');
                if (intentParams) {
                    closeIntentSheet();
                }
                stopStatusCheck();
                toast.error('Payment verification timeout. Please check your transaction status.');
                if (onPaymentError) onPaymentError('Payment verification timeout');
                return;
            }

            try {
                const statusResult = await checkStatus();
                console.log('Status check result:', statusResult);
                if (statusResult !== null) {
                    if (intentParams) {
                        closeIntentSheet();
                    }
                    stopStatusCheck();
                }
            } catch (error) {
                console.error('Status check error:', error);
                if (intentParams) {
                    closeIntentSheet();
                }
                stopStatusCheck();
            }
        }, CHECK_INTERVAL);
    };

    const stopStatusCheck = () => {
        console.log('Stopping status check');
        setIsStatusCheckActive(false);
        setLoading(false);

        if (statusCheckIntervalRef.current) {
            clearInterval(statusCheckIntervalRef.current);
            statusCheckIntervalRef.current = null;
        }
    };

    const closeIntentSheet = () => {
        setShowIntentSheet(false);
        stopStatusCheck();
    };

    // If no payment result or processing, don't render anything
    if (!result || isProcessing) {
        return null;
    }

    if (razorpayComplete) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => { }}
                />

                {/* Modal Card */}
                <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
                    <div className="flex flex-col items-center">
                        {/* Loading Spinner */}
                        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />

                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                            Processing Payment
                        </h3>
                        <p className="text-gray-600 text-center mb-4">
                            Please wait while we process your payment of ₹{result?.order_info?.amount}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {showIntentSheet && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    {/* Overlay */}
                    <div
                        className="fixed inset-0 bg-black/50 transition-opacity"
                        onClick={closeIntentSheet}
                    />

                    {/* Sheet */}
                    <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl max-h-[85%] overflow-y-auto">
                        <div className="p-4">
                            {/* Drag Indicator */}
                            <div className="flex justify-center mb-4">
                                <div className="w-12 h-1 bg-gray-300 rounded-full" />
                            </div>

                            {/* Close Button */}
                            <button
                                onClick={closeIntentSheet}
                                className="absolute top-3 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <IoClose size={24} className="text-gray-600" />
                            </button>

                            {/* Header */}
                            <div className="text-center mb-6">
                                <h2 className="text-xl font-bold text-gray-800">
                                    Choose UPI App
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Select your preferred UPI payment app
                                </p>
                            </div>

                            {/* UPI Apps Grid */}
                            <div className="grid grid-cols-4 gap-4 mb-6">
                                {apps.map((app) => (
                                    <button
                                        key={app.id}
                                        onClick={() => handleAppSelect(app)}
                                        disabled={loading}
                                        className="flex flex-col items-center p-3 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <div className="mb-2">
                                            {app.icon}
                                        </div>
                                        <span className="text-xs font-medium text-gray-700 text-center">
                                            {app.name}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Cancel Button */}
                            <div className="mt-4">
                                <button
                                    onClick={closeIntentSheet}
                                    disabled={isStatusCheckActive}
                                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${isStatusCheckActive
                                        ? 'bg-blue-600 text-white opacity-50 cursor-not-allowed'
                                        : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                                        }`}
                                >
                                    {isStatusCheckActive ? 'Checking status...' : 'Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Toaster />
        </>
    );
};